'use client';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useContext } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaShieldAlt,
} from 'react-icons/fa';
import { useAccount } from 'wagmi';
import { SignatureContext } from '../context/SignatureContext';
import ConfirmWalletButton from './ConfirmWalletButton';

const WalletConfirmationOverlay: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { signature } = useContext(SignatureContext);

  if (!isConnected || signature) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/95 p-4 backdrop-blur-sm'
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className='relative my-auto w-full max-w-lg overflow-hidden rounded-xl border border-gray-700/50 bg-gray-800/50 p-4 backdrop-blur-sm sm:p-6'
        >
          {/* Animated gradient background */}
          <motion.div
            className="absolute inset-0 opacity-10"
            animate={{
              background: [
                'radial-gradient(circle at 0% 0%, #2563eb 0%, transparent 50%)',
                'radial-gradient(circle at 100% 100%, #2563eb 0%, transparent 50%)',
                'radial-gradient(circle at 0% 0%, #2563eb 0%, transparent 50%)',
              ],
            }}
            transition={{
              duration: 8,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
            }}
          />

          {/* Content */}
          <div className="relative z-10">
            <div className='mb-4 flex items-center gap-3 sm:mb-6'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/20 sm:h-12 sm:w-12'>
                <FaShieldAlt className="text-blue-400 text-lg sm:text-xl" />
              </div>
              <h2 className='font-bold text-white text-xl sm:text-2xl'>
                Wallet Confirmation Required
              </h2>
            </div>

            {/* Wallet Safety Message */}
            <div className='mb-4 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 sm:mb-6 sm:p-4'>
              <p className='text-blue-300 text-xs sm:text-sm'>
                <span className="font-semibold">
                  Safe to connect any wallet:
                </span>{' '}
                This app only requires a signature to verify ownership. Your
                token holdings are checked without needing access to funds, so
                you can safely connect any wallet - even an empty one.
              </p>
            </div>

            {/* Security Checklist */}
            <div className='mb-4 space-y-3 sm:mb-6 sm:space-y-4'>
              <SecurityCheckItem
                icon={<FaCheckCircle />}
                text="Verify you're on www.baseboomer.com"
                highlight={window.location.hostname === 'www.baseboomer.com'}
              />
              <SecurityCheckItem
                icon={<FaCheckCircle />}
                text="No funds or token approvals required"
                highlight={true}
              />
              <SecurityCheckItem
                icon={<FaExclamationTriangle />}
                text="Never sign messages on unofficial domains"
                isWarning={true}
              />
              <SecurityCheckItem
                icon={<FaShieldAlt />}
                text="This signature only proves wallet ownership"
              />
            </div>

            {/* Connected Address */}
            <div className='mb-4 rounded-lg border border-gray-700/30 bg-gray-900/30 p-3 sm:mb-6 sm:p-4'>
              <div className='mb-1 text-gray-400 text-xs sm:text-sm'>
                Connected Address:
              </div>
              <div className='break-all font-mono text-gray-200 text-sm sm:text-base'>
                {address}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <ConfirmWalletButton address={address as `0x${string}`} />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Security Check Item Component
const SecurityCheckItem = ({
  icon,
  text,
  highlight = false,
  isWarning = false,
}: {
  icon: React.ReactNode;
  text: string;
  highlight?: boolean;
  isWarning?: boolean;
}) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className={`flex items-center gap-2 rounded-lg p-2 sm:gap-3 sm:p-3 ${
      highlight
        ? 'border border-green-500/20 bg-green-500/10'
        : isWarning
          ? 'border border-yellow-500/20 bg-yellow-500/10'
          : 'bg-gray-700/20'
    }`}
  >
    <span
      className={
        highlight
          ? 'text-green-400'
          : isWarning
            ? 'text-yellow-400'
            : 'text-gray-400'
      }
    >
      {icon}
    </span>
    <span
      className={`text-xs sm:text-sm ${
        highlight
          ? 'text-green-300'
          : isWarning
            ? 'text-yellow-300'
            : 'text-gray-300'
      }`}
    >
      {text}
    </span>
  </motion.div>
);

export default WalletConfirmationOverlay;
